<template>
  <div v-if="currentStep === step">
    <form @submit.prevent="handleNextStep">
      <div class="d-flex flex-column align-items-center">
        <img :src="getTemplatePath(step - 1)" alt="Plantilla seleccionada" class="img-thumbnail small-image mb-5" />
      </div>
      <div class="mb-3 d-flex justify-content-center">
        <div class="form-check form-check-inline">
          <input class="form-check-input" type="radio" :id="'optionImage' + step" value="image"
            v-model="localSelectedOption" @change="onOptionChange" />
          <label class="form-check-label" :for="'optionImage' + step">Imagen</label>
        </div>
        <div class="form-check form-check-inline">
          <input class="form-check-input" type="radio" :id="'optionText' + step" value="text"
            v-model="localSelectedOption" @change="onOptionChange" />
          <label class="form-check-label" :for="'optionText' + step">Texto</label>
        </div>
      </div>

      <div v-if="localSelectedOption === 'image'">
        <FileUploadPreview accept="image/*,video/webm" label="Imagen" :id="'image' + step" :file="localForm.image"
          :preview="localForm.image" :required="!isEditing" @file-change="handleFileChange"
          no-file-message="No se ha seleccionado ninguna imagen" />
      </div>

      <div v-else-if="localSelectedOption === 'text'">
        <div :class="{ 'editor-type1': sectionType === 1 }">
          <div class="mb-3">
            <label for="background-color" class="form-label">Color de fondo del texto</label>
            <input type="color" id="background-color" class="form-control-color" v-model="background_color" />
          </div>
          <div class="mb-3">
            <label :for="'content' + step" class="form-label">Contenido</label>
            <quill-editor ref="quillEditor" v-model:content="localForm.content" :theme="'snow'"
              class="form-control editor-container" :style="{ backgroundColor: background_color }" @input="onInputChange"
              @text-change="onTextChange" @ready="onEditorReady" toolbar="full" :modules="modules"
              :options="editorOptions"></quill-editor>
          </div>
        </div>
      </div>

    </form>
  </div>
</template>

<script>
import { ref, watch, computed } from "vue";
import axios from "@/plugins/axios";
import FileUploadPreview from "@/components/shared/FileUploadPreview.vue";
import { QuillEditor } from "@vueup/vue-quill";
import BlotFormatter from "quill-blot-formatter";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import "@/plugins/quill-image-format";

export default {
  components: {
    FileUploadPreview,
    QuillEditor,
  },
  props: {
    step: Number,
    currentStep: Number,
    form: Object,
    selectedOption: String,
    getTemplatePath: Function,
    isEditing: Boolean,
    steps: Array,
    selectedTemplate: Object,
    sectionType: { type: Number, default: null },
  },
  emits: ["prevStep", "nextStep", "update", "validateStep"],
  setup(props, { emit }) {
    const localSelectedOption = ref(props.selectedOption);
    const localForm = ref({ ...props.form });
    const background_color = ref("#ffffff");
    const quillEditor = ref(null);
    const editorReady = ref(false);

    const modules = {
      name: "blotFormatter",
      module: BlotFormatter,
      options: {
        /* options */
      },
      imageCompress: {
        quality: 0.7,
        maxWidth: 1000,
        maxHeight: 1000,
        imageType: "image/jpeg",
        debug: true,
        suppressErrorLogging: false,
        insertIntoEditor: undefined,
      },
    };

    const editorOptions = ref({
      modules: {
        name: "blotFormatter",
        module: BlotFormatter,
        options: {
          /* options */
        },
        imageCompress: {
          quality: 0.7,
          maxWidth: 1000,
          maxHeight: 1000,
          imageType: "image/jpeg",
          debug: true,
          suppressErrorLogging: false,
          insertIntoEditor: undefined,
        },
      },
    });

    const uploadImageToServer = async (file) => {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post("/images/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data.url;
    };

    const setupQuillImageHandlers = (quillInstance) => {
      const toolbar = quillInstance.getModule("toolbar");
      toolbar.addHandler("image", () => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");
        input.click();
        input.onchange = async () => {
          const file = input.files[0];
          if (!file) return;
          const url = await uploadImageToServer(file);
          const range = quillInstance.getSelection(true);
          quillInstance.insertEmbed(range.index, "image", url);
          quillInstance.setSelection(range.index + 1);
        };
      });

      quillInstance.root.addEventListener("paste", async (e) => {
        const items = e.clipboardData?.items ?? [];
        for (const item of items) {
          if (item.type.startsWith("image/")) {
            e.preventDefault();
            const file = item.getAsFile();
            if (!file) break;
            const url = await uploadImageToServer(file);
            const range = quillInstance.getSelection(true);
            quillInstance.insertEmbed(range.index, "image", url);
            quillInstance.setSelection(range.index + 1);
            break;
          }
        }
      });
    };

    const onEditorReady = () => {
      editorReady.value = true;

      if (quillEditor.value) {
        const quillInstance = quillEditor.value.getQuill();

        if (typeof localForm.value.content === "string") {
          quillInstance.clipboard.dangerouslyPasteHTML(localForm.value.content);
        } else if (
          localForm.value.content &&
          typeof localForm.value.content === "object" &&
          localForm.value.content.ops
        ) {
          quillInstance.setContents(localForm.value.content);
        } else {
          localForm.value.content = "";
          quillInstance.clipboard.dangerouslyPasteHTML("");
        }

        setupQuillImageHandlers(quillInstance);
      }
    };

    const resetFormContent = () => {
      localForm.value.content = "";
      localForm.value.image = null;
    };

    const validateStep = () => {
      const isValid =
        (localSelectedOption.value === "image" && !!localForm.value.image) ||
        (localSelectedOption.value === "text" && !!localForm.value.content);

      emit("validateStep", { step: props.step, isValid });
    };

    const emitUpdate = (key, value) => {
      localForm.value[key] = value;
      emit("update", {
        key,
        value,
        background_color: background_color.value,
        step: props.step,
      });
      validateStep();
    };

    const onTextChange = () => {
      emitUpdate("content", localForm.value.content, props.step);
    };

    watch(background_color, () => {
      emitUpdate("content", localForm.value.content, props.step);
    });

    watch(
      () => props.selectedTemplate,
      (newTemplate) => {
        if (newTemplate) {
          resetFormContent();
        }
      }
    );

    watch(
      () => props.form,
      (newForm) => {
        if (newForm.content && typeof newForm.content === "string") {
          background_color.value = newForm.background_color || "#ffffff";
          localForm.value.content = newForm.content;
          localSelectedOption.value = "text";
        } else if (newForm.image) {
          localForm.value.image = newForm.image;
          localSelectedOption.value = "image";
        }
        validateStep();
      },
      { immediate: true }
    );

    const extractPlainText = (content) => {
      if (!content) return "";
      if (typeof content === "string") return content;
      if (content.ops) {
        return content.ops
          .map((op) => (typeof op.insert === "string" ? op.insert : ""))
          .join("");
      }
      return "";
    };

    const onOptionChange = () => {
      if (localSelectedOption.value === "text") {
        localForm.value.image = null;
      } else if (localSelectedOption.value === "image") {
        localForm.value.content = "";
      }
      emitUpdate("selectedOption", localSelectedOption.value);
    };

    const handleFileChange = (file) => {
      localForm.value.image = file;
      emitUpdate("image", localForm.value.image, props.step);
    };

    const onInputChange = () => {
      emitUpdate("content", localForm.value.content, props.step);
    };

    const isNextDisabled = computed(() => {
      validateStep();

      const plainText = extractPlainText(localForm.value.content);

      if (localSelectedOption.value === "image" && !localForm.value.image) {
        return true;
      }

      if (localSelectedOption.value === "text" && !plainText.trim()) {
        return true;
      }

      return false;
    });

    return {
      localSelectedOption,
      localForm,
      emitUpdate,
      handleFileChange,
      onInputChange,
      onOptionChange,
      isNextDisabled,
      background_color,
      quillEditor,
      onEditorReady,
      editorReady,
      onTextChange,
      editorOptions,
      modules,
    };
  },
};
</script>

<style scoped>
.small-image {
  max-width: 150px;
  max-height: 150px;
  width: auto;
  height: auto;
}

/* Importar la fuente Montserrat */
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap");

/* Definir la fuente Montserrat para Quill */
@font-face {
  font-family: "Montserrat";
  src: url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap");
}

::v-deep .ql-editor {
  min-height: 200px;
  max-height: 600px;
  overflow-y: auto;
  font-family: 'Montserrat', sans-serif;
}

::v-deep .ql-font-montserrat {
  font-family: "Montserrat", sans-serif;
}

.editor-container {
  display: flex;
  flex-direction: column;
}

.editor-type1 {
  max-width: 1080px;
  margin: 0 auto;
}

.editor-type1 ::v-deep .ql-editor {
  color: #757575;
  font-size: 20px;
  line-height: 30px;
}


</style>
